import { AlertTriangleIcon } from '@langchain/untitled-ui-icons';
import { LinearProgress } from '@mui/joy';

import ReactMarkdown from 'react-markdown';
import { Link } from 'react-router-dom';
import rehypeSanitize from 'rehype-sanitize';

import { Error } from '@/components/Hub/Error';
import { HubCode } from '@/components/Hub/HubCode';
import { NoData } from '@/components/Prompts/PromptsTable/PromptsNoData';
import { OrgConfigs, useOrgConfig } from '@/hooks/useOrgConfig';
import { usePermissions } from '@/hooks/usePermissions.tsx';
import { useOrganizationId } from '@/hooks/useSwr';
import ChainIcon from '@/icons/ChainIcon.svg?react';
import ReadmeIcon from '@/icons/ReadmeIcon.svg?react';
import { addQueryParams } from '@/utils/add-query-params';
import { appHubIndexPath, appPromptsIndexPath } from '@/utils/constants';

import { OptimizePrompt } from '../Playground/Optimization/OptimizePrompt';
import PlaygroundExample from '../Playground/components/PlaygroundExample';
import { ManifestHub } from '../Playground/components/manifest/serialized/ManifestHub';
import {
  getPromptManifest,
  isRunnablePrompt,
} from '../Playground/utils/Playground.utils';
import { HubCard } from './HubCard';
import { ModelConfig } from './ModelConfig';
import { useRepoData } from './hooks/useRepoData';

const RepoContent = ({
  inHub,
  showProxyProviderWarning,
}: {
  inHub: boolean;
  showProxyProviderWarning?: boolean;
  setShowProxyProviderWarningModal: (open: boolean) => void;
}) => {
  const {
    error,
    loading,
    hasCommits,
    commitObj,
    owner,
    repo,
    commit,
    selectedTenant,
    repoObj,
  } = useRepoData(inHub);
  const organizationId = useOrganizationId();
  const { authorize } = usePermissions();

  const { value: promptOptimizationJobsEnabled } = useOrgConfig(
    OrgConfigs.prompt_optimization_jobs_enabled
  );

  if (loading) {
    return <LinearProgress />;
  }

  if (error) {
    return <Error>{error.message}</Error>;
  }

  if (!repoObj || (hasCommits && !commitObj)) {
    return null;
  }
  const isPublic = repoObj?.repo.is_public;

  const editable =
    selectedTenant?.id === repoObj.repo.tenant_id &&
    authorize('prompts:update');
  // todo: delete? commmit examples are no longer a thing
  const examples =
    commitObj?.examples.sort(
      (a, b) => Date.parse(a.start_time) - Date.parse(b.start_time)
    ) ?? [];

  return (
    <div className="my-auto h-full grow p-6 px-4">
      {!hasCommits || !commitObj ? (
        <NoData />
      ) : (
        <div className="mx-auto flex w-full max-w-[1024px] flex-col space-y-4">
          {repoObj.repo.readme ? (
            <HubCard
              title="Readme"
              icon={<ReadmeIcon />}
              actions={
                editable ? (
                  <Link
                    to={addQueryParams(
                      `/${
                        inHub
                          ? appHubIndexPath + '/' + owner
                          : appPromptsIndexPath
                      }/${repo}/edit`,
                      { organizationId }
                    )}
                    className="ml-auto flex gap-2 rounded-lg border border-secondary px-3 py-1 text-right text-sm font-medium text-ls-black transition-all hover:bg-secondary-hover active:bg-secondary"
                  >
                    Edit
                  </Link>
                ) : null
              }
            >
              <div className="dark:prose-invert prose max-w-none prose-headings:font-semibold prose-h1:text-[20px] prose-h2:text-[18px] prose-h3:text-[16px] prose-h4:text-[14px] prose-h5:text-[14px] prose-h6:text-[14px] prose-pre:whitespace-pre-wrap">
                <ReactMarkdown skipHtml rehypePlugins={[rehypeSanitize]}>
                  {repoObj.repo?.readme ?? ''}
                </ReactMarkdown>
              </div>
            </HubCard>
          ) : null}
          {showProxyProviderWarning && (
            <div className="rounded-lg border border-secondary bg-primary p-4">
              <div className="flex flex-col gap-1">
                <div className="flex flex-row items-center gap-1 text-warning">
                  <AlertTriangleIcon className="h-4 w-4" />
                  <span className="text-sm font-medium text-primary">
                    Note: This prompt uses a proxy provider.
                  </span>
                </div>
                <span className="text-sm">
                  It may be possible for the owner to see sensitive data, such
                  as your API keys.
                </span>
              </div>
            </div>
          )}
          <ManifestHub
            key={commitObj.commit_hash}
            value={getPromptManifest(commitObj.manifest)}
            options={{}}
            cardProps={{
              actions: promptOptimizationJobsEnabled && (
                <OptimizePrompt
                  initialPromptIdentifier={repoObj.repo.repo_handle}
                  disabledMessage={
                    repoObj.repo.num_commits == 0
                      ? 'Add a commit to optimize'
                      : ''
                  }
                />
              ),
            }}
          />
          {isRunnablePrompt(commitObj.manifest.id) && (
            <ModelConfig model={commitObj.manifest.kwargs.last} />
          )}
          <HubCode
            owner={repoObj?.repo?.owner}
            repo={repo}
            commit={commit}
            isPublic={isPublic}
          />
          {examples.map((example, index) => (
            <HubCard
              title={'Example ' + (index + 1)}
              icon={<ChainIcon className="h-4 w-4" />}
              key={index}
            >
              <PlaygroundExample
                index={index}
                example={example}
                showHeader={false}
              />
            </HubCard>
          ))}
        </div>
      )}
    </div>
  );
};

export default RepoContent;
